import * as React from 'react'
import { Repository, Team } from '@basehub/data/replicache/index'
import { Section, SectionSeparator } from './section'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { EditorSettingPageTitle } from '../../../_components/setting-page-title'
import { DeployWithVercel } from '../../../branch/_components/timeline/deploy'
import { READMEScreenshots } from '../../../branch/_components/timeline/screenshots'
import { READMEAdditionalInfo } from '../../../branch/_components/timeline/additional-info'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { Contributors } from '~/app/(dash)/[team-slug]/(team)/components/repositories/repository/contributors'
import { tabContentWrapperId } from '../../../tab-content/wrapper'
import { DefaultBranchActions } from '../../../branch/_components/timeline'
import { useForkOrigin } from '~/hooks-data/use-fork-origin'

export const RepositoryPublicInformation = ({
  repository,
  team,
  visitor,
}: {
  repository: Repository | PublicRepo
  team: Team | PublicTeam
  visitor?: boolean
}) => {
  if (visitor) return <PublicREADME repository={repository} team={team} />

  return (
    <div>
      <EditorSettingPageTitle>Public information</EditorSettingPageTitle>

      {Boolean(!repository.isPublic) && (
        <div className="mb-4">
          <PrivateRepoIndicator />
        </div>
      )}

      <Section
        title="Deploy with Vercel"
        subtitle="Set up the Vercel Deploy Button"
      >
        <DeployWithVercel repo={repository} team={team} visitor={false} />
      </Section>
      <SectionSeparator visitor={false} />
      <Section
        title={
          (repository.readme?.screenshots?.length ?? 0) > 0
            ? 'Featured Media'
            : ''
        }
        subtitle="Upload screenshots and demo videos showcasing your repository"
      >
        <READMEScreenshots repo={repository} team={team} visitor={false} />
      </Section>
      <SectionSeparator visitor={false} />
      <Section
        title="Additional info"
        subtitle="Add additional information about this repository"
      >
        <READMEAdditionalInfo repo={repository} visitor={false} />
      </Section>
    </div>
  )
}

const PrivateRepoIndicator = () => {
  return (
    <div className="rounded-lg border border-dark-border-solid py-3 px-4 bg-dark-base-dark max-w-[632px]">
      <p className="mb-1 font-medium text-sm">Your repository is private</p>
      <p className="text-dark-muted text-xs">
        This information will be visible if you make your repo Public.
      </p>
    </div>
  )
}

const PublicREADME = ({
  team,
  repository,
}: {
  team: PublicTeam | Team
  repository: Repository | PublicRepo
}) => {
  const { data: forkOrigin } = useForkOrigin(repository, team)
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null)
  React.useEffect(() => {
    const containerEl = document.getElementById(
      tabContentWrapperId
    ) as HTMLDivElement | null
    if (containerEl) {
      setContainer(containerEl)
    }
  }, [])

  return (
    <div className="pb-40">
      <div className="flex justify-between items-start gap-x-4">
        <div className="flex-1 overflow-hidden pl-1 -ml-1 mb-4">
          <h2 className="text-2xl mb-1 font-medium leading-[1.3] text-dark-bright">
            {repository?.title}

            <span className="ml-2 text-dark-faint font-medium text-xs tracking-normal">
              {repository?.isPublic ? 'Public' : 'Private'}
            </span>
          </h2>
          {repository?.description && (
            <p className="text-dark-muted unset w-full cursor-text">
              {repository?.description ?? ''}
            </p>
          )}
          {repository.forkStatus && (
            <div className="mt-2 text-xs text-dark-muted">
              {forkOrigin ? (
                <>
                  Forked from{' '}
                  <ButtonLink
                    unstyled
                    focusStyled
                    href={`/${forkOrigin.teamSlug}/${forkOrigin.repoSlug}`}
                    className="text-dark-control-base hover:underline"
                  >
                    {forkOrigin.teamSlug}/{forkOrigin.repoSlug}
                  </ButtonLink>
                </>
              ) : (
                <>Loading fork info...</>
              )}
            </div>
          )}
        </div>
        <DefaultBranchActions
          repository={repository}
          team={team}
          forkPrimaryButton
        />
      </div>

      <div className="flex items-center mb-12">
        <Contributors
          className="mr-auto pointer-events-auto"
          contributorsIds={repository?.contributors}
          collisionBoundary={container}
          team={team}
          repository={repository}
          avatarSize={28}
          showedContributors={10}
        />
      </div>

      {/* All else should be wrapped in this div because of the translate */}
      <div>
        <Section title="">
          <DeployWithVercel repo={repository} team={team} visitor />
        </Section>
        <SectionSeparator visitor />
        <Section title="">
          <READMEScreenshots repo={repository} team={team} visitor />
        </Section>
        <SectionSeparator visitor />
        <Section title="">
          <READMEAdditionalInfo repo={repository} visitor />
        </Section>
      </div>
    </div>
  )
}
